/**
 * /admin/ops/infra — Infrastructure Control Plane (FM).
 *
 * Shows real-time health of all external providers:
 * Netlify · Neon · Resend · R2 · GitHub · Kapso · MercadoPago
 *
 * Super_admin only.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState, useCallback, type CSSProperties } from "react";
import { OpsSubnav } from "@/components/OpsSubnav";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const Route = createFileRoute("/admin/ops/infra")({
  component: InfraStatusPage,
});

// --- Types ---------------------------------------------------------------

type ProviderHealth = "healthy" | "warning" | "critical" | "offline" | "unknown" | "not_configured";

const CATEGORY_LABELS: Record<string, string> = {
  hosting: "Hosting",
  database: "Base de datos",
  messaging: "Mensajería",
  storage: "Storage",
  scm: "Código fuente",
  payments: "Pagos",
};

// Espejo de PROVIDER_CATEGORY (src/lib/runtime-providers/index.ts) — server-only,
// no se puede importar directo en un componente de ruta cliente.
const PROVIDER_CATEGORY: Record<string, string> = {
  netlify: "hosting",
  neon: "database",
  resend: "messaging",
  r2: "storage",
  github: "scm",
  kapso: "messaging",
  mercadopago: "payments",
};

interface ProviderMetric {
  key: string;
  label: string;
  value: number | string | null;
  unit?: string;
  used?: number;
  limit?: number;
  pct?: number;
}

interface ProviderAlert {
  level: "info" | "warning" | "critical";
  code: string;
  message: string;
  metric?: string;
}

interface ProviderStatus {
  provider: string;
  label: string;
  status: ProviderHealth;
  metrics: ProviderMetric[];
  alerts: ProviderAlert[];
  meta?: Record<string, unknown>;
  updatedAt: string;
  refreshMs?: number;
  error?: string;
}

interface InfraScore {
  ok: boolean;
  overall: ProviderHealth;
  healthy: number;
  warning: number;
  critical: number;
  offline: number;
  unknown: number;
  notConfigured: number;
  providers: ProviderStatus[];
  computedAt: string;
}

// --- Helpers -------------------------------------------------------------

const STATUS_COLOR: Record<ProviderHealth, string> = {
  healthy: "#22c55e",
  warning: "#f59e0b",
  critical: "#ef4444",
  offline: "#6b7280",
  unknown: "#94a3b8",
  not_configured: "#94a3b8",
};

const STATUS_BG: Record<ProviderHealth, string> = {
  healthy: "#f0fdf4",
  warning: "#fffbeb",
  critical: "#fef2f2",
  offline: "#f9fafb",
  unknown: "#f8fafc",
  not_configured: "#f8fafc",
};

const STATUS_LABEL: Record<ProviderHealth, string> = {
  healthy: "healthy",
  warning: "warning",
  critical: "critical",
  offline: "offline",
  unknown: "unknown",
  not_configured: "no configurado",
};

const ALERT_COLOR: Record<ProviderAlert["level"], string> = {
  info: "#3b82f6",
  warning: "#f59e0b",
  critical: "#ef4444",
};

const PROVIDER_ICON: Record<string, string> = {
  netlify: "🌐",
  neon: "🐘",
  resend: "📧",
  r2: "☁️",
  github: "🐙",
  kapso: "💬",
  mercadopago: "💳",
};

function fmtDate(iso: string) {
  try {
    return new Date(iso).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "medium" });
  } catch { return iso; }
}

function ageLabel(iso: string) {
  const m = (Date.now() - new Date(iso).getTime()) / 60_000;
  if (m < 1) return "hace <1min";
  if (m < 60) return `hace ${Math.round(m)}min`;
  return `hace ${(m / 60).toFixed(0)}h`;
}

// --- Sub-components ------------------------------------------------------

function StatusDot({ status }: { status: ProviderHealth }) {
  return (
    <span
      style={{
        display: "inline-block",
        width: 10,
        height: 10,
        borderRadius: "50%",
        background: STATUS_COLOR[status],
        marginRight: 6,
        flexShrink: 0,
      }}
    />
  );
}

function StatusBadge({ status }: { status: ProviderHealth }) {
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: STATUS_COLOR[status],
        background: STATUS_BG[status],
        border: `1px solid ${STATUS_COLOR[status]}33`,
        borderRadius: 4,
        padding: "2px 7px",
      }}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

function MetricBar({ pct }: { pct: number }) {
  const color = pct >= 90 ? "#ef4444" : pct >= 70 ? "#f59e0b" : "#22c55e";
  return (
    <div style={{ flex: 1, height: 6, background: "#e5e7eb", borderRadius: 3, overflow: "hidden" }}>
      <div style={{ width: `${Math.min(100, pct)}%`, height: "100%", background: color, borderRadius: 3, transition: "width 0.3s" }} />
    </div>
  );
}

const SECRET_KEY_RE = /password|secret|token|api[_-]?key|credential/i;

/** live/cached/snapshot — derivado de la edad real del dato y si vino de un refresh forzado (SCRUM-311). */
function dataSourceLabel(p: ProviderStatus, wasForcedRefresh: boolean): string {
  const ageMin = (Date.now() - new Date(p.updatedAt).getTime()) / 60_000;
  if (wasForcedRefresh && ageMin < 1) return "en vivo (recién verificado)";
  if (ageMin < 15) return "cache reciente (memoria, TTL 15min)";
  if (ageMin < 45) return "snapshot (DB, TTL 45min)";
  return "snapshot desactualizado";
}

function humanizeKey(key: string): string {
  return key.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}

function DetailDialog({ p, wasForcedRefresh, onClose }: { p: ProviderStatus; wasForcedRefresh: boolean; onClose: () => void }) {
  const quotaMetrics = p.metrics.filter((m) => m.limit != null);
  const infoMetrics = p.metrics.filter((m) => m.limit == null);
  const metaEntries = Object.entries(p.meta ?? {}).filter(
    ([k, v]) => v != null && v !== "" && !SECRET_KEY_RE.test(k),
  );

  const sectionTitle: CSSProperties = { fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {PROVIDER_ICON[p.provider] ?? "🔌"} {p.label}
          </DialogTitle>
        </DialogHeader>
        <div style={{ fontFamily: "system-ui, sans-serif" }}>
          {/* Estado y configuración */}
          <div style={{ marginBottom: 14 }}>
            <div style={sectionTitle}>Estado y configuración</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <StatusDot status={p.status} />
              <StatusBadge status={p.status} />
              <span style={{ fontSize: 11, color: "#9ca3af" }}>{CATEGORY_LABELS[PROVIDER_CATEGORY[p.provider] ?? ""] ?? ""}</span>
            </div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>Última verificación: {fmtDate(p.updatedAt)} ({ageLabel(p.updatedAt)})</div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>Fuente del dato: {dataSourceLabel(p, wasForcedRefresh)}</div>
            {p.refreshMs != null && <div style={{ fontSize: 12, color: "#6b7280" }}>Latencia del probe: {p.refreshMs}ms</div>}
          </div>

          {/* Alertas */}
          {p.alerts.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={sectionTitle}>Alertas</div>
              {p.alerts.map((a, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 6, marginBottom: 4, fontSize: 13 }}>
                  <span style={{ color: ALERT_COLOR[a.level], flexShrink: 0 }}>
                    {a.level === "critical" ? "❌" : a.level === "warning" ? "⚠️" : "ℹ️"}
                  </span>
                  <span style={{ color: "#374151" }}>{a.message}</span>
                </div>
              ))}
            </div>
          )}

          {/* Quotas / límites */}
          <div style={{ marginBottom: 14 }}>
            <div style={sectionTitle}>Cuotas / límites</div>
            {quotaMetrics.length === 0 ? (
              <p style={{ fontSize: 12, color: "#9ca3af", fontStyle: "italic" }}>No disponible para este provider.</p>
            ) : (
              quotaMetrics.map((m) => (
                <div key={m.key} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, fontSize: 13 }}>
                  <span style={{ color: "#6b7280", minWidth: 160 }}>{m.label}</span>
                  <span style={{ fontWeight: 500 }}>{m.value}{m.unit ? ` ${m.unit}` : ""}</span>
                  {m.pct != null && (
                    <>
                      <MetricBar pct={m.pct} />
                      <span style={{ fontSize: 11, color: "#6b7280", minWidth: 32 }}>{m.pct}%</span>
                    </>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Uso / info */}
          <div style={{ marginBottom: 14 }}>
            <div style={sectionTitle}>Uso / información</div>
            {infoMetrics.length === 0 ? (
              <p style={{ fontSize: 12, color: "#9ca3af", fontStyle: "italic" }}>No disponible para este provider.</p>
            ) : (
              infoMetrics.map((m) => (
                <div key={m.key} style={{ display: "flex", gap: 8, marginBottom: 4, fontSize: 13 }}>
                  <span style={{ color: "#6b7280", minWidth: 160 }}>{m.label}</span>
                  <span style={{ fontWeight: 500 }}>{m.value != null ? `${m.value}${m.unit ? ` ${m.unit}` : ""}` : "—"}</span>
                </div>
              ))
            )}
          </div>

          {/* Eventos / metadata recientes */}
          <div style={{ marginBottom: 14 }}>
            <div style={sectionTitle}>Eventos recientes</div>
            {metaEntries.length === 0 ? (
              <p style={{ fontSize: 12, color: "#9ca3af", fontStyle: "italic" }}>No disponible para este provider.</p>
            ) : (
              metaEntries.map(([k, v]) => (
                <div key={k} style={{ display: "flex", gap: 8, marginBottom: 4, fontSize: 12 }}>
                  <span style={{ color: "#9ca3af", minWidth: 160 }}>{humanizeKey(k)}</span>
                  <span style={{ color: "#374151", wordBreak: "break-all" }}>{String(v)}</span>
                </div>
              ))
            )}
          </div>

          {/* Error */}
          {p.error && (
            <div style={{ padding: "6px 10px", background: "#fef2f2", borderRadius: 5, fontSize: 12, color: "#b91c1c", fontFamily: "monospace" }}>
              {p.error.slice(0, 300)}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProviderCard({
  p,
  onRefresh,
  onOpenDetail,
}: {
  p: ProviderStatus;
  onRefresh: (provider: string) => void;
  onOpenDetail: (provider: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      style={{
        border: `1.5px solid ${STATUS_COLOR[p.status]}44`,
        borderRadius: 10,
        background: "#fff",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "12px 16px",
          background: STATUS_BG[p.status],
          cursor: "pointer",
          userSelect: "none",
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <span style={{ fontSize: 20 }}>{PROVIDER_ICON[p.provider] ?? "🔌"}</span>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <StatusDot status={p.status} />
            <span style={{ fontWeight: 600, fontSize: 15 }}>{p.label}</span>
            <StatusBadge status={p.status} />
          </div>
          {p.alerts.length > 0 && (
            <div style={{ marginTop: 3, fontSize: 12, color: ALERT_COLOR[p.alerts[0].level] }}>
              {p.alerts[0].message}
              {p.alerts.length > 1 && <span style={{ color: "#6b7280" }}> +{p.alerts.length - 1} más</span>}
            </div>
          )}
          {p.alerts.length === 0 && p.metrics[0] && (
            <div style={{ marginTop: 3, fontSize: 12, color: "#6b7280" }}>
              {p.metrics[0].label}: {p.metrics[0].value}{p.metrics[0].unit ? ` ${p.metrics[0].unit}` : ""}
            </div>
          )}
        </div>
        <div style={{ textAlign: "right", fontSize: 11, color: "#9ca3af" }}>
          <div>{ageLabel(p.updatedAt)}</div>
          {p.refreshMs != null && <div>{p.refreshMs}ms</div>}
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onOpenDetail(p.provider); }}
          style={{ fontSize: 12, background: "none", border: "1px solid #d1d5db", borderRadius: 5, padding: "3px 10px", cursor: "pointer", color: "#374151" }}
          title="Ver detalle operativo"
        >
          Detalle
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onRefresh(p.provider); }}
          style={{ fontSize: 13, background: "none", border: "1px solid #d1d5db", borderRadius: 5, padding: "3px 8px", cursor: "pointer", color: "#374151" }}
          title="Refrescar este provider"
        >
          ↻
        </button>
        <span style={{ fontSize: 12, color: "#9ca3af" }}>{expanded ? "▲" : "▼"}</span>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div style={{ padding: "12px 16px", borderTop: "1px solid #f3f4f6" }}>
          {/* Alerts */}
          {p.alerts.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>Alertas</div>
              {p.alerts.map((a, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 6, marginBottom: 4, fontSize: 13 }}>
                  <span style={{ color: ALERT_COLOR[a.level], flexShrink: 0 }}>
                    {a.level === "critical" ? "❌" : a.level === "warning" ? "⚠️" : "ℹ️"}
                  </span>
                  <span style={{ color: "#374151" }}>{a.message}</span>
                  <code style={{ fontSize: 10, color: "#9ca3af", marginLeft: "auto", flexShrink: 0 }}>{a.code}</code>
                </div>
              ))}
            </div>
          )}

          {/* Metrics */}
          {p.metrics.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>Métricas</div>
              {p.metrics.map((m) => (
                <div key={m.key} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5, fontSize: 13 }}>
                  <span style={{ color: "#6b7280", minWidth: 180 }}>{m.label}</span>
                  <span style={{ fontWeight: 500, color: "#111827" }}>
                    {m.value != null ? `${m.value}${m.unit ? ` ${m.unit}` : ""}` : "—"}
                  </span>
                  {m.limit != null && m.used != null && (
                    <>
                      <span style={{ color: "#9ca3af", fontSize: 11 }}>/ {m.limit} {m.unit}</span>
                      {m.pct != null && (
                        <>
                          <MetricBar pct={m.pct} />
                          <span style={{ fontSize: 11, color: "#6b7280", minWidth: 36 }}>{m.pct}%</span>
                        </>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Error */}
          {p.error && (
            <div style={{ marginTop: 8, padding: "6px 10px", background: "#fef2f2", borderRadius: 5, fontSize: 12, color: "#b91c1c", fontFamily: "monospace" }}>
              {p.error.slice(0, 200)}
            </div>
          )}

          <div style={{ marginTop: 8, fontSize: 11, color: "#9ca3af" }}>
            Actualizado: {fmtDate(p.updatedAt)}
          </div>
        </div>
      )}
    </div>
  );
}

// --- Main page -----------------------------------------------------------

function InfraStatusPage() {
  const [data, setData] = useState<InfraScore | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<Date | null>(null);
  const [forcedProviders, setForcedProviders] = useState<Set<string>>(new Set());
  const [detailProvider, setDetailProvider] = useState<string | null>(null);

  const loadData = useCallback(async (live = false) => {
    setLoading(true);
    setError(null);
    try {
      const url = live ? "/api/admin/infra-status?live=true" : "/api/admin/infra-status";
      const res = await fetch(url);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Error desconocido");
      setData(json);
      setLastFetch(new Date());
      if (live) setForcedProviders(new Set((json.providers ?? []).map((p: ProviderStatus) => p.provider)));
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshProvider = useCallback(async (provider: string) => {
    setRefreshing(provider);
    try {
      const res = await fetch("/api/admin/infra-status", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ provider }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Error");
      setForcedProviders((prev) => new Set(prev).add(provider));
      // Merge updated provider into existing data
      if (json.provider && data) {
        setData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            providers: prev.providers.map((p) =>
              p.provider === json.provider.provider ? json.provider : p,
            ),
          };
        });
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setRefreshing(null);
    }
  }, [data]);

  useEffect(() => { loadData(false); }, [loadData]);

  const healthyCnt = data?.healthy ?? 0;
  const warnCnt = data?.warning ?? 0;
  const critCnt = data?.critical ?? 0;
  const offCnt = (data?.offline ?? 0) + (data?.unknown ?? 0);
  const notConfiguredCnt = data?.notConfigured ?? 0;
  const overall = data?.overall ?? "unknown";

  // Sort: problemas primero. "not_configured" es neutral, va al final junto a healthy.
  const sortOrder: Record<ProviderHealth, number> = { critical: 0, offline: 1, warning: 2, unknown: 3, healthy: 4, not_configured: 5 };
  const sorted = [...(data?.providers ?? [])].sort((a, b) => sortOrder[a.status] - sortOrder[b.status]);

  const grouped: [string, ProviderStatus[]][] = [];
  {
    const byCategory = new Map<string, ProviderStatus[]>();
    for (const p of sorted) {
      const cat = PROVIDER_CATEGORY[p.provider] ?? "otros";
      const arr = byCategory.get(cat) ?? [];
      arr.push(p);
      byCategory.set(cat, arr);
    }
    grouped.push(...byCategory.entries());
  }

  return (
    <div style={{ maxWidth: 860, margin: "0 auto", padding: "24px 16px", fontFamily: "system-ui, sans-serif" }}>
      <OpsSubnav hint="Estado de dependencias/integraciones propias de la plataforma (no de tenants)." />
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#111827" }}>
            <StatusDot status={overall} /> Infra Control Plane
          </h1>
          <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
            {lastFetch ? `Última carga: ${fmtDate(lastFetch.toISOString())}` : "Cargando…"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => loadData(false)}
            disabled={loading}
            style={{ fontSize: 13, padding: "6px 14px", border: "1px solid #d1d5db", borderRadius: 6, cursor: loading ? "not-allowed" : "pointer", background: "#fff", color: "#374151" }}
          >
            ↻ Cache
          </button>
          <button
            onClick={() => loadData(true)}
            disabled={loading}
            style={{ fontSize: 13, padding: "6px 14px", border: "1px solid #6366f1", borderRadius: 6, cursor: loading ? "not-allowed" : "pointer", background: "#6366f1", color: "#fff", fontWeight: 600 }}
          >
            {loading ? "Cargando…" : "⚡ Refrescar todo"}
          </button>
        </div>
      </div>

      {/* Score bar */}
      {data && (
        <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
          {[
            { label: "Saludables", count: healthyCnt, color: "#22c55e" },
            { label: "Warnings", count: warnCnt, color: "#f59e0b" },
            { label: "Críticos", count: critCnt, color: "#ef4444" },
            { label: "Offline/desconocidos", count: offCnt, color: "#6b7280" },
            { label: "No configurados", count: notConfiguredCnt, color: "#94a3b8" },
          ].map((s) => (
            <div
              key={s.label}
              style={{
                flex: "1 0 auto",
                minWidth: 120,
                padding: "10px 14px",
                background: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: 26, fontWeight: 700, color: s.color }}>{s.count}</div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>{s.label}</div>
            </div>
          ))}
          <div
            style={{
              flex: "1 0 auto",
              minWidth: 120,
              padding: "10px 14px",
              background: STATUS_BG[overall],
              border: `1.5px solid ${STATUS_COLOR[overall]}55`,
              borderRadius: 8,
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 700, color: STATUS_COLOR[overall], textTransform: "uppercase" }}>{overall}</div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>Estado global</div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ marginBottom: 16, padding: "10px 14px", background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, color: "#b91c1c", fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Provider cards */}
      {loading && !data && (
        <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>Cargando providers…</div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {grouped.map(([category, providers]) => (
          <div key={category}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
              {CATEGORY_LABELS[category] ?? category}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {providers.map((p) => (
                <div key={p.provider} style={{ opacity: refreshing === p.provider ? 0.6 : 1, transition: "opacity 0.2s" }}>
                  <ProviderCard p={p} onRefresh={refreshProvider} onOpenDetail={setDetailProvider} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Notes */}
      <div style={{ marginTop: 24, padding: "12px 14px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12, color: "#64748b" }}>
        <strong>Nota:</strong> Los datos en caché se leen de memoria (TTL 15min) o de la tabla <code>sm_provider_snapshots</code> (TTL 45min).
        "⚡ Refrescar todo" dispara fetch en vivo de todos los providers. Los providers sin credenciales aparecen como <em>no configurado</em> (neutral, no cuenta como falla).
      </div>

      {detailProvider && data ? (
        (() => {
          const p = data.providers.find((x) => x.provider === detailProvider);
          if (!p) return null;
          return <DetailDialog p={p} wasForcedRefresh={forcedProviders.has(detailProvider)} onClose={() => setDetailProvider(null)} />;
        })()
      ) : null}
    </div>
  );
}
